import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Key, ArrowRight, CheckCircle, Building2 } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { authAPI } from '../services/api';
import Input from '../modern-components/ui/Input';
import Button from '../modern-components/ui/Button';
import Card from '../modern-components/ui/Card';

const Login = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter mobile, 2: Enter OTP
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Step 1: Mobile number form
  const mobileFormik = useFormik({
    initialValues: {
      mobile: ''
    },
    validationSchema: Yup.object({
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
        .required('Mobile number is required')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess('');

      try {
        const response = await authAPI.sendOTP(values.mobile);
        if (response.data.success) {
          setMobile(values.mobile);
          setStep(2);
          setSuccess('OTP sent successfully to your mobile number');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to send OTP');
      } finally {
        setLoading(false);
      }
    }
  });

  // Step 2: OTP verification form
  const otpFormik = useFormik({
    initialValues: {
      otp: ''
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .length(6, 'OTP must be 6 digits')
        .required('OTP is required')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const response = await authAPI.verifyOTP(mobile, values.otp);
        if (response.data.success) {
          // Save token and user data
          localStorage.setItem('token', response.data.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.data.user));

          // Always redirect to dashboard after login
          navigate('/', { replace: true });
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to verify OTP');
      } finally {
        setLoading(false);
      }
    }
  });

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.sendOTP(mobile);
      setSuccess('OTP resent successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-secondary-500 rounded-full opacity-10 -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-500 rounded-full opacity-10 -ml-48 -mb-48"></div>

        {/* Logo and Branding */}
        <div className="relative z-10">
          <div className="mb-8">
            <img src="/logo.png" alt="Loha Mart" className="h-12 w-auto object-contain" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
            Welcome to India's Leading<br />
            <span className="text-secondary-500">Industrial Materials Marketplace</span>
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            Access premium quality steel, construction materials, and industrial supplies with competitive pricing and flexible payment options.
          </p>
        </div>

        {/* Features */}
        <div className="relative z-10 space-y-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-white font-semibold mb-1">Quality Assured Products</h3>
              <p className="text-gray-300 text-sm">All materials tested and certified to meet industry standards</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-white font-semibold mb-1">Competitive Pricing</h3>
              <p className="text-gray-300 text-sm">Best market rates with bulk order discounts</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-white font-semibold mb-1">Fast Delivery</h3>
              <p className="text-gray-300 text-sm">Pan-India delivery network with real-time tracking</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Loha Mart" className="h-12 w-auto object-contain" />
          </div>

          <Card className="shadow-xl">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {step === 1 ? 'Sign In' : 'Verify OTP'}
              </h2>
              <p className="text-gray-600">
                {step === 1
                  ? 'Enter your mobile number to get started'
                  : `We've sent a 6-digit code to ${mobile}`
                }
              </p>
            </div>

            {/* Alerts */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-slide-down">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg animate-slide-down">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            {/* Step 1: Mobile Number Form */}
            {step === 1 ? (
              <form onSubmit={mobileFormik.handleSubmit} className="space-y-6">
                <Input
                  label="Mobile Number"
                  type="tel"
                  id="mobile"
                  name="mobile"
                  placeholder="Enter 10 digit mobile number"
                  value={mobileFormik.values.mobile}
                  onChange={mobileFormik.handleChange}
                  error={mobileFormik.touched.mobile && mobileFormik.errors.mobile}
                  leftIcon={<Phone className="w-5 h-5" />}
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={loading}
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                >
                  Send OTP
                </Button>

                <p className="text-center text-sm text-gray-600">
                  By continuing, you agree to our{' '}
                  <a href="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                    Privacy Policy
                  </a>
                </p>
              </form>
            ) : (
              /* Step 2: OTP Verification Form */
              <form onSubmit={otpFormik.handleSubmit} className="space-y-6">
                <Input
                  label="Enter OTP"
                  type="text"
                  id="otp"
                  name="otp"
                  placeholder="Enter 6 digit OTP"
                  value={otpFormik.values.otp}
                  onChange={otpFormik.handleChange}
                  error={otpFormik.touched.otp && otpFormik.errors.otp}
                  leftIcon={<Key className="w-5 h-5" />}
                  disabled={loading}
                  maxLength={6}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={loading}
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                >
                  Verify & Continue
                </Button>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setError('');
                      setSuccess('');
                      otpFormik.resetForm();
                    }}
                    className="text-sm text-gray-600 hover:text-primary-600 font-medium transition-colors"
                    disabled={loading}
                  >
                    Change Number
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                </div>
              </form>
            )}
          </Card>

          {/* Register as Seller */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600 mb-4">
              Want to sell on Loha Mart?
            </p>
            <a
              href={`${process.env.REACT_APP_SELLER_URL || 'http://localhost:3002'}/register`}
              className="flex items-center justify-center gap-3 p-4 bg-gradient-to-br from-secondary-50 to-secondary-100 border-2 border-secondary-200 rounded-xl hover:border-secondary-400 hover:shadow-md transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-sm font-semibold text-secondary-700 block">Register as Seller</span>
                <span className="text-xs text-gray-500">Start selling your products</span>
              </div>
            </a>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-600 mt-6">
            Need help? Contact us at{' '}
            <a href="tel:+911234567890" className="text-primary-600 hover:text-primary-700 font-medium">
              +91 123 456 7890
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
